import { Alert, Box, Typography } from '@mui/material';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { BookingFlowResumer } from '../../../booking-flow/BookingFlowResumer';
import { BookingFlowStepper } from '../../../booking-flow/BookingFlowStepper';
import { useAuthStore } from '../../../auth/authStore';
import { ApprovalPassengerStep } from '../../../components/booking/ApprovalPassengerStep';
import {
  approvalEmployeeId,
  corporateMaxBudget,
  needsCorporateApproval,
} from '../../../components/booking/corporateApproval';
import { useSentForApproval } from '../../../components/booking/useSentForApproval';
import { transferFlowConfig, type TransferFlowState } from '../flowConfig';
import { transferResumeAdapter } from '../resume';
import { TransferSearchStep } from '../components/TransferSearchStep';
import { TransferResultsStep } from '../components/TransferResultsStep';
import { TransferPassengerStep } from '../components/TransferPassengerStep';
import { TransferPrebookStep } from '../components/TransferPrebookStep';
import { TransferPaymentStep } from '../components/TransferPaymentStep';
import { TransferFinalizeStep } from '../components/TransferFinalizeStep';
import { TransferDocumentStep } from '../components/TransferDocumentStep';

function TransferFlowInner() {
  const flow = useBookingFlowContext<TransferFlowState>();
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);

  useSentForApproval({
    sent: !!flow.state.sentForApproval,
    idle: flow.status === 'idle',
    currentStepId: flow.currentStep?.id,
    passengerStepId: 'updatePassengers',
    reservationPath: '/transfer/reservations',
    reset: flow.reset,
    message:
      'Transfer onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  });

  const sr = flow.state.searchRequest;
  const gate = {
    cart: flow.state.cart,
    searchEmployeeId: sr?.employeeID,
    extraOverBudget: flow.state.selectedVehicle?.isOutBudget,
    extraMaxBudget: flow.state.selectedVehicle?.maxBudget,
  };
  const approvalEmpId = approvalEmployeeId(gate, sessionEmployeeId);
  const singlePax = sr?.adtPaxCount === 1 && !sr?.chdPaxCount && !sr?.infPaxCount;
  const passengerStepIsApproval = singlePax && needsCorporateApproval(gate) && !!approvalEmpId;

  return (
    <Box>
      <BookingFlowStepper />
      {flow.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {flow.error}
        </Alert>
      )}
      {flow.currentStep.id === 'search' && <TransferSearchStep />}
      {flow.currentStep.id === 'allocate' && <TransferResultsStep />}
      {flow.currentStep.id === 'updatePassengers' &&
        (passengerStepIsApproval ? (
          <ApprovalPassengerStep
            employeeId={approvalEmpId as number}
            maxBudget={corporateMaxBudget(gate)}
            productLabel="transfer"
            loading={flow.status === 'running'}
            onSubmit={(passengers, contact, billingInformation) => {
              flow
                .runStep({ passengers, contact, billingInformation, sentForApproval: true })
                .catch(() => undefined);
            }}
          />
        ) : (
          <TransferPassengerStep />
        ))}
      {flow.currentStep.id === 'prebook' && <TransferPrebookStep />}
      {flow.currentStep.id === 'payment' && <TransferPaymentStep />}
      {flow.currentStep.id === 'finalize' && <TransferFinalizeStep />}
      {flow.currentStep.id === 'document' && <TransferDocumentStep />}
    </Box>
  );
}

export default function TransferFlowPage() {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Transfer
      </Typography>
      <BookingFlowResumer config={transferFlowConfig} adapter={transferResumeAdapter}>
        <TransferFlowInner />
      </BookingFlowResumer>
    </Box>
  );
}
